import { useFormContext } from "react-hook-form";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Input,
  ModalBody,
  ModalContent,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";

import { FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { cn } from "../../utils/cn";
import type { TokenDetails } from "../../types/token.types";
import { AssetImage } from "../AssetImage";

import { Button } from "@heroui/react";
import { Modal } from "../ui/modal";

import { SelectableTokenList } from "./SelectableTokenList";

const priorityTokens = [
  "EGLD",
  "USDC",
  "USDT",
  "SEGLD",
  "WBTC",
  "WETH",
  "HTM",
  "MEX",
  "TADA",
  "ITHEUM",
  "ASH",
  "DAI",
  "RIDE",
  "UTK",
  "CYBER",
  "ONE",
  "CRT",
];

const prioritizeToken = (token: TokenDetails) => {
  const index = priorityTokens.indexOf(token.identifier.split("-")[0]);
  return index !== -1 ? index : priorityTokens.length;
};

interface InputProps {
  name: string;
  tokens: TokenDetails[];
  hiddenTokens?: string[];
  label?: string;
  withoutErrorMessage?: boolean;
  placeholder?: string;
  className?: string;
  defaultValue?: TokenDetails | null;
}

interface RootProps extends React.HTMLAttributes<HTMLDivElement> {}

interface Props {
  select: InputProps;
  root?: RootProps;
}

export const ControlledTokenSelectModal = ({ select, root }: Props) => {
  const form = useFormContext();
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const searchDeferred = useDeferredValue(search);
  const inputRef = useRef<HTMLInputElement>(null);

  const tokens = useMemo(() => {
    if (!select.tokens || select.tokens.length === 0) {
      return [];
    }
    
    const sortedTokens = select.tokens.sort((a, b) => {
      return prioritizeToken(a) - prioritizeToken(b);
    });

    const filtered = sortedTokens.filter((token) => {
      const matchesSearch = searchDeferred === '' || 
        token.identifier.toLowerCase().includes(searchDeferred.toLowerCase()) ||
        token.identifier.split('-')[0].toLowerCase().includes(searchDeferred.toLowerCase());
      const notHidden = !select.hiddenTokens?.includes(token.identifier);
      return matchesSearch && notHidden;
    });
    return filtered;
  }, [searchDeferred, select.hiddenTokens, select.tokens]);

  const findItemByIdentifier = (identifier?: string) => {
    if (!identifier) {
      return undefined;
    }
    const item = select.tokens.find((token) => token.identifier === identifier);
    return item;
  };

  useEffect(() => {
    if (select.tokens && select.tokens.length > 0) {
      if (select.defaultValue !== undefined) {
        const identifier = select.defaultValue?.identifier;
        const item = findItemByIdentifier(identifier);
        if (item) {
          form.setValue(select.name, item);
        }
        return;
      }
      const token = select.tokens[0];
      if (token) {
        form.setValue(select.name, token);
      }
    }
  }, [select.tokens, select.defaultValue, select.name, form]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      const token = tokens[0];
      form.setValue(select.name, token);
      onClose();
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      inputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div
      className="button-disabled-cursor-default flex flex-col space-y-1"
      {...root}
    >
      <div className={cn("flex  rounded-md ")}>
        <FormField
          control={form.control}
          name={select.name}
          render={({ field }) => {
            return (
              <FormItem className={cn("flex-1", select.className)}>
                {select.label && <FormLabel>{select.label}</FormLabel>}
                <Button
                  variant="flat"
                  type="button"
                  className={cn(
                    "w-full rounded-lg flex justify-between items-center h-auto px-4 py-2 bg-white/10 dark:bg-white/10",
                    !field.value && "text-default-500"
                  )}
                  onPress={onOpen}
                >
                  {!field.value && select.placeholder}
                  {field.value && (
                    <div className="flex items-center gap-2">
                      <AssetImage
                        identifier={field.value?.identifier}
                        className="w-6 h-6 min-w-6 flex-shrink-0 flex-grow-1"
                      />
                      <p className="text-base font-bold text-foreground">
                        {field.value?.identifier?.split("-")[0]}
                      </p>
                    </div>
                  )}
                  <ChevronDownIcon className="w-4 h-4 text-default-500" />
                </Button>
                <Modal
                  isKeyboardDismissDisabled
                  isOpen={isOpen}
                  onOpenChange={onOpenChange}
                >
                  <ModalContent className="">
                    <ModalHeader className="dark:text-white text-black">
                      Select Token
                    </ModalHeader>
                    <ModalBody className="p-2">
                      <Input
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        value={search}
                        placeholder="Search token"
                        startContent={
                          <MagnifyingGlassIcon className="w-4 h-4  " />
                        }
                      />
                    </ModalBody>
                    <SelectableTokenList
                      tokens={tokens}
                      name={select.name}
                      onClick={onClose}
                    />
                  </ModalContent>
                </Modal>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
    </div>
  );
};
